import { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  FileIcon,
  Video,
  Loader2,
  ExternalLink,
  MessageSquare,
  Send,
  Edit2,
  CheckCircle,
  Clock,
  AlertCircle,
  Calendar,
  Copy,
  Eye,
  Users,
  ChevronDown,
  Search,
  Globe,
  Upload,
  Link as LinkIcon,
} from "lucide-react";
import { showToast } from "@/components/ui/Toast";
import {
  Deliverable,
  DeliverableComment,
  ReviewLink,
  DraftBlogPost,
  BlogStatus,
  updateDeliverable,
  getDeliverableComments,
  addDeliverableComment,
  getDeliverableReviewLinks,
  getDraftBlogPosts,
  getBlogStatus,
  publishBlog,
} from "@/actions/delivery-hub";

// Helper: convert URLs in text to clickable links
const linkifyText = (text: string) => {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand underline hover:text-brand-hover break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

interface DeliverableDetailSliderProps {
  isOpen: boolean;
  onClose: () => void;
  deliverable: Deliverable | null;
  schoolId: number;
  onUpdate?: (updated: Deliverable) => void;
}

type DeliverableType = "blog" | "video";

const deliverableTypes: {
  value: DeliverableType;
  label: string;
  icon: typeof FileIcon;
  color: string;
}[] = [
  { value: "blog", label: "Blog Post", icon: FileIcon, color: "text-brand bg-brand/10" },
  { value: "video", label: "Video", icon: Video, color: "text-pink-600 bg-pink-50" },
];

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const months = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

const DeliverableDetailSlider = ({
  isOpen,
  onClose,
  deliverable,
  schoolId,
  onUpdate,
}: DeliverableDetailSliderProps) => {
  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    type: "blog" as DeliverableType,
    url: "",
    delivery_month: new Date().getMonth() + 1,
    delivery_year: new Date().getFullYear(),
  });
  const [saving, setSaving] = useState(false);

  // Comments state
  const [comments, setComments] = useState<DeliverableComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);

  // Review links state
  const [reviewLinks, setReviewLinks] = useState<ReviewLink[]>([]);
  const [reviewLinksLoading, setReviewLinksLoading] = useState(false);

  // Blog status state (for publish feature)
  const [blogStatus, setBlogStatus] = useState<BlogStatus | null>(null);
  const [blogStatusLoading, setBlogStatusLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Draft posts state (for blog type editing)
  const [draftPosts, setDraftPosts] = useState<DraftBlogPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [selectedPost, setSelectedPost] = useState<DraftBlogPost | null>(null);
  const [postDropdownOpen, setPostDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPosts, setTotalPosts] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Animation states
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle animation
  useEffect(() => {
    if (isOpen && !shouldRender) {
      setShouldRender(true);
      setTimeout(() => {
        setIsAnimating(true);
      }, 50);
    } else if (!isOpen && shouldRender) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsEditing(false);
        setNewComment("");
        setSelectedPost(null);
        setDraftPosts([]);
        setSearchQuery("");
        setPostDropdownOpen(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  // Load deliverable data into edit form
  useEffect(() => {
    if (deliverable) {
      setEditForm({
        title: deliverable.title,
        description: deliverable.description || "",
        type: deliverable.type,
        url: deliverable.url || "",
        delivery_month: deliverable.delivery_month || new Date().getMonth() + 1,
        delivery_year: deliverable.delivery_year || new Date().getFullYear(),
      });
    }
  }, [deliverable]);

  // Fetch comments, review links, and blog status when opened
  useEffect(() => {
    if (isOpen && deliverable) {
      fetchComments();
      fetchReviewLinks();
      if (deliverable.type === "blog") {
        fetchBlogStatus();
      }
    }
  }, [isOpen, deliverable]);

  // Fetch review links
  const fetchReviewLinks = async () => {
    if (!deliverable) return;
    setReviewLinksLoading(true);
    const data = await getDeliverableReviewLinks(deliverable.id);
    setReviewLinks(data);
    setReviewLinksLoading(false);
  };

  // Fetch blog status (for publish feature)
  const fetchBlogStatus = async () => {
    if (!deliverable) return;
    setBlogStatusLoading(true);
    const status = await getBlogStatus(deliverable.id);
    setBlogStatus(status);
    setBlogStatusLoading(false);
  };

  // Handle publish blog
  const handlePublishBlog = async () => {
    if (!deliverable) return;

    if (deliverable.status !== "approved") {
      showToast("Deliverable must be approved before publishing", "error");
      return;
    }

    setPublishing(true);
    const result = await publishBlog(deliverable.id);

    if (result.success) {
      showToast(result.message, "success");
      // Refresh blog status
      await fetchBlogStatus();
    } else {
      showToast(result.message, "error");
    }
    setPublishing(false);
  };

  // Fetch draft posts when editing blog type
  const fetchDraftPosts = useCallback(async (search?: string) => {
    if (!schoolId) return;
    setLoadingPosts(true);
    const result = await getDraftBlogPosts(schoolId, search);
    setDraftPosts(result.posts);
    setTotalPosts(result.totalPosts);
    setLoadingPosts(false);
  }, [schoolId]);

  // Fetch draft posts when entering edit mode for blog type
  useEffect(() => {
    if (isEditing && editForm.type === "blog" && schoolId) {
      fetchDraftPosts();
    }
  }, [isEditing, editForm.type, schoolId, fetchDraftPosts]);

  // Debounced search for draft posts
  useEffect(() => {
    if (!postDropdownOpen || editForm.type !== "blog") return;

    const timer = setTimeout(() => {
      fetchDraftPosts(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, postDropdownOpen, editForm.type, fetchDraftPosts]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setPostDropdownOpen(false);
      }
    };

    if (postDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [postDropdownOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (postDropdownOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [postDropdownOpen]);

  const handlePostSelect = (post: DraftBlogPost) => {
    setSelectedPost(post);
    setEditForm({
      ...editForm,
      title: post.title,
      url: post.url,
    });
    setPostDropdownOpen(false);
    setSearchQuery("");
  };

  const copyReviewLink = (url: string) => {
    navigator.clipboard.writeText(url);
    showToast("Review link copied to clipboard", "success");
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
            <CheckCircle size={12} />
            Approved
          </span>
        );
      case "needs_revision":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
            <AlertCircle size={12} />
            Needs Revision
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
            <Clock size={12} />
            Pending
          </span>
        );
    }
  };

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const fetchComments = async () => {
    if (!deliverable) return;
    setCommentsLoading(true);
    const data = await getDeliverableComments(deliverable.id);
    setComments(data);
    setCommentsLoading(false);
  };

  const handleSave = async () => {
    if (!deliverable) return;
    if (!editForm.title.trim()) {
      showToast("Title is required", "error");
      return;
    }

    setSaving(true);
    const result = await updateDeliverable(deliverable.id, {
      title: editForm.title,
      description: editForm.description || undefined,
      type: editForm.type,
      url: editForm.url || undefined,
      delivery_month: editForm.delivery_month,
      delivery_year: editForm.delivery_year,
    });

    if (result) {
      showToast("Deliverable updated", "success");
      setIsEditing(false);
      onUpdate?.(result);
    } else {
      showToast("Failed to update deliverable", "error");
    }
    setSaving(false);
  };

  const handleAddComment = async () => {
    if (!deliverable || !newComment.trim()) return;

    setAddingComment(true);
    const result = await addDeliverableComment(deliverable.id, newComment.trim());
    if (result) {
      setComments((prev) => [result, ...prev]);
      setNewComment("");
      showToast("Comment added", "success");
    } else {
      showToast("Failed to add comment", "error");
    }
    setAddingComment(false);
  };

  const handleStatusChange = async (newStatus: Deliverable["status"]) => {
    if (!deliverable) return;
    const result = await updateDeliverable(deliverable.id, { status: newStatus });
    if (result) {
      showToast(`Status changed to ${newStatus.replace("_", " ")}`, "success");
      onUpdate?.(result);
    } else {
      showToast("Failed to update status", "error");
    }
  };

  if (!shouldRender || !deliverable) return null;

  const selectedType = deliverableTypes.find((t) => t.value === (isEditing ? editForm.type : deliverable.type));

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 cursor-pointer ${
          isAnimating ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl transition-transform duration-300 ease-out ${
          isAnimating ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedType?.color}`}>
                {selectedType && <selectedType.icon size={20} />}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {isEditing ? "Edit Deliverable" : "Deliverable Details"}
                </h2>
                <p className="text-sm text-gray-500 capitalize">{selectedType?.label}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 text-gray-500 hover:text-brand hover:bg-gray-100 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit2 size={18} />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className={`flex-1 ${isEditing ? 'overflow-y-auto' : 'overflow-hidden'}`}>
            {isEditing ? (
              // Edit Form
              <div className="p-6 space-y-4">
                {/* Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <div className="flex gap-2">
                    {deliverableTypes.map((type) => {
                      const Icon = type.icon;
                      const isSelected = editForm.type === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => {
                            setEditForm({ ...editForm, type: type.value });
                            setSelectedPost(null);
                          }}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                            isSelected
                              ? type.color + " border-current"
                              : "border-gray-200 hover:border-gray-300 text-gray-600"
                          }`}
                        >
                          <Icon size={18} />
                          <span className="text-sm font-medium">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Month Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar size={14} className="inline mr-1" />
                    Delivery Month
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={editForm.delivery_month}
                      onChange={(e) => setEditForm({ ...editForm, delivery_month: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                    >
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={editForm.delivery_year}
                      onChange={(e) => setEditForm({ ...editForm, delivery_year: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                    >
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Draft Blog Posts Selector (only for blog type) */}
                {editForm.type === "blog" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Draft Blog Post
                    </label>
                    <div className="relative" ref={dropdownRef}>
                      <button
                        type="button"
                        onClick={() => {
                          setPostDropdownOpen(!postDropdownOpen);
                          if (!postDropdownOpen) {
                            setSearchQuery("");
                          }
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-left hover:border-gray-400 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          {selectedPost ? (
                            <span className="text-sm text-gray-900 truncate block">{selectedPost.title}</span>
                          ) : (
                            <span className="text-sm text-gray-500">Search draft posts...</span>
                          )}
                        </div>
                        <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${postDropdownOpen ? "rotate-180" : ""}`} />
                      </button>

                      {/* Dropdown with Search */}
                      {postDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                          {/* Search Input */}
                          <div className="p-2 border-b border-gray-100">
                            <div className="relative">
                              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                ref={searchInputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Type to search drafts..."
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-brand focus:border-brand"
                              />
                            </div>
                          </div>

                          {/* Results */}
                          <div className="max-h-60 overflow-y-auto">
                            {loadingPosts ? (
                              <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
                                <Loader2 size={16} className="animate-spin" />
                                Searching...
                              </div>
                            ) : draftPosts.length === 0 ? (
                              <div className="py-4 text-center text-sm text-gray-500">
                                {searchQuery ? `No drafts found for "${searchQuery}"` : "No draft posts available"}
                              </div>
                            ) : (
                              <>
                                {draftPosts.map((post) => (
                                  <button
                                    key={post.id}
                                    type="button"
                                    onClick={() => handlePostSelect(post)}
                                    className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                                      selectedPost?.id === post.id ? "bg-brand/5" : ""
                                    }`}
                                  >
                                    <p className="text-sm text-gray-900 font-medium truncate">{post.title}</p>
                                    <p className="text-xs text-gray-500 truncate mt-0.5">{post.slug}</p>
                                  </button>
                                ))}
                                {totalPosts > draftPosts.length && (
                                  <div className="px-3 py-2 text-xs text-gray-400 bg-gray-50 text-center">
                                    Showing {draftPosts.length} of {totalPosts} posts. Type to search more.
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Search and select a draft, or enter details manually below
                    </p>
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => {
                      setEditForm({ ...editForm, title: e.target.value });
                      if (selectedPost) setSelectedPost(null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                  />
                </div>

                {/* URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                  <div className="relative">
                    <LinkIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="url"
                      value={editForm.url}
                      onChange={(e) => {
                        setEditForm({ ...editForm, url: e.target.value });
                        if (selectedPost) setSelectedPost(null);
                      }}
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                      placeholder="https://..."
                    />
                    {editForm.url && (
                      <a
                        href={editForm.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand"
                        title="Open in new tab"
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand resize-none"
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-hover disabled:opacity-50"
                  >
                    {saving && <Loader2 size={16} className="mr-2 animate-spin" />}
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              // View Mode - with sticky comment input
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-6">
                {/* Title & Description */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{deliverable.title}</h3>
                {deliverable.description && (
                  <p className="text-sm text-gray-600 mb-4">{deliverable.description}</p>
                )}

                {/* URL & Delivery Month - Single Line */}
                <div className="flex items-center gap-4 mb-4 flex-wrap">
                  {deliverable.review_token && (
                    <a
                      href={`${window.location.origin}/review/${deliverable.review_token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-brand hover:underline"
                    >
                      <ExternalLink size={14} />
                      View Deliverable
                    </a>
                  )}
                  {(deliverable.delivery_month && deliverable.delivery_year) && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg">
                      <Calendar size={14} className="text-gray-400" />
                      <span>
                        {months.find(m => m.value === deliverable.delivery_month)?.label} {deliverable.delivery_year}
                      </span>
                    </div>
                  )}
                </div>

                {/* Blog Status & Publish (only for blog type) */}
                {deliverable.type === "blog" && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
                          <Globe size={20} className="text-gray-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Blog Status</p>
                          {blogStatusLoading ? (
                            <p className="text-xs text-gray-500">Loading...</p>
                          ) : blogStatus?.blog_status === "published" ? (
                            <p className="text-xs text-green-600 font-medium">Published</p>
                          ) : blogStatus?.blog_status === "draft" ? (
                            <p className="text-xs text-amber-600 font-medium">Draft (Not Published)</p>
                          ) : (
                            <p className="text-xs text-gray-500">Unknown</p>
                          )}
                        </div>
                      </div>

                      {/* Publish Button */}
                      {blogStatus?.blog_status === "draft" && (
                        <button
                          onClick={handlePublishBlog}
                          disabled={publishing || deliverable.status !== "approved"}
                          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            deliverable.status === "approved"
                              ? "bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                              : "bg-gray-200 text-gray-500 cursor-not-allowed"
                          }`}
                          title={deliverable.status !== "approved" ? "Deliverable must be approved first" : "Publish blog"}
                        >
                          {publishing ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Upload size={16} />
                          )}
                          Publish Blog
                        </button>
                      )}

                      {blogStatus?.blog_status === "published" && deliverable.url && (
                        <a
                          href={`https://yellowslate.com/blog/${deliverable.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand bg-white border border-brand rounded-lg hover:bg-brand/5"
                        >
                          <ExternalLink size={16} />
                          View Live
                        </a>
                      )}
                    </div>

                    {deliverable.status !== "approved" && blogStatus?.blog_status === "draft" && (
                      <p className="mt-3 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded">
                        Deliverable must be approved before you can publish the blog.
                      </p>
                    )}
                  </div>
                )}

                {/* Status */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleStatusChange("approved")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        deliverable.status === "approved"
                          ? "bg-green-100 text-green-700 border border-green-300"
                          : "text-gray-600 hover:bg-gray-100 border border-gray-200"
                      }`}
                    >
                      <CheckCircle size={14} />
                      Approved
                    </button>
                    <button
                      onClick={() => handleStatusChange("pending")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        deliverable.status === "pending"
                          ? "bg-amber-100 text-amber-700 border border-amber-300"
                          : "text-gray-600 hover:bg-gray-100 border border-gray-200"
                      }`}
                    >
                      <Clock size={14} />
                      Pending
                    </button>
                    <button
                      onClick={() => handleStatusChange("needs_revision")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        deliverable.status === "needs_revision"
                          ? "bg-red-100 text-red-700 border border-red-300"
                          : "text-gray-600 hover:bg-gray-100 border border-gray-200"
                      }`}
                    >
                      <AlertCircle size={14} />
                      Needs Revision
                    </button>
                  </div>
                </div>

                {/* Review Links Section */}
                <div className="mb-6 border-t pt-4">
                  <h4 className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-3">
                    <Users size={16} />
                    Review Links ({reviewLinks.length})
                  </h4>

                  {reviewLinksLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 size={20} className="animate-spin text-gray-400" />
                    </div>
                  ) : reviewLinks.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No review links generated yet. Send a notification to create review links.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {reviewLinks.map((link) => (
                        <div key={link.id} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {link.contact?.name || "Unknown Contact"}
                                </span>
                                {getStatusBadge(link.review_status)}
                              </div>
                              {link.contact?.designation && (
                                <p className="text-xs text-gray-500">{link.contact.designation}</p>
                              )}
                            </div>
                            <button
                              onClick={() => copyReviewLink(link.review_url)}
                              className="p-1.5 text-gray-400 hover:text-brand hover:bg-white rounded transition-colors"
                              title="Copy review link"
                            >
                              <Copy size={14} />
                            </button>
                          </div>

                          {/* Review URL */}
                          {link.review_url && (
                            <div className="flex items-center gap-2 mb-2">
                              <LinkIcon size={12} className="text-gray-400 flex-shrink-0" />
                              <a
                                href={link.review_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-brand hover:underline truncate"
                                title={link.review_url}
                              >
                                {link.review_url}
                              </a>
                            </div>
                          )}

                          {/* Timestamps */}
                          <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-2">
                            {link.sent_at && (
                              <span className="flex items-center gap-1">
                                <Send size={10} />
                                Sent: {formatDate(link.sent_at)}
                              </span>
                            )}
                            {link.viewed_at && (
                              <span className="flex items-center gap-1">
                                <Eye size={10} />
                                Viewed: {formatDate(link.viewed_at)}
                              </span>
                            )}
                            {link.reviewed_at && (
                              <span className="flex items-center gap-1">
                                <CheckCircle size={10} />
                                Reviewed: {formatDate(link.reviewed_at)}
                              </span>
                            )}
                          </div>

                          {/* Review Comment */}
                          {link.review_comment && (
                            <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                              <p className="text-xs font-medium text-gray-600 mb-1">Client Feedback:</p>
                              <p className="text-sm text-gray-700">{link.review_comment}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Meta Info */}
                <div className="text-xs text-gray-500 mb-6">
                  <p>Created: {formatDate(deliverable.created_at)}</p>
                  {deliverable.approved_at && (
                    <p>Approved: {formatDate(deliverable.approved_at)}</p>
                  )}
                </div>

                {/* Comments Section - Header & List */}
                <div className="border-t pt-4">
                  <h4 className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-4">
                    <MessageSquare size={16} />
                    Comments ({comments.length})
                  </h4>

                  {/* Comments List */}
                  {commentsLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 size={20} className="animate-spin text-gray-400" />
                    </div>
                  ) : comments.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No comments yet</p>
                  ) : (
                    <div className="space-y-3">
                      {comments.map((comment) => (
                        <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {comment.user_name}
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatDate(comment.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{linkifyText(comment.comment)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                </div>

                {/* Sticky Comment Input Footer */}
                <div className="border-t bg-white p-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={addingComment || !newComment.trim()}
                      className="px-4 py-2 text-white bg-brand rounded-lg hover:bg-brand-hover disabled:opacity-50 flex items-center gap-2"
                    >
                      {addingComment ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Send size={18} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliverableDetailSlider;
